import { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuthReady } from '@/hooks/useAuthReady';
import { supabase } from '@/integrations/supabase/client';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
}

export const ProtectedRoute = ({ children, requireAdmin = false }: ProtectedRouteProps) => {
  const { user, isAdmin } = useAuth();
  const { isActive } = useSubscription();
  const { isReady } = useAuthReady();
  const location = useLocation();
  const navigate = useNavigate();
  const [onboardingComplete, setOnboardingComplete] = useState(true);

  // Fetch AI Twin onboarding status
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user?.id) return;
      
      const { data } = await supabase
        .from('ai_twin_profiles')
        .select('onboarding_completed')
        .eq('user_id', user.id)
        .maybeSingle();
      
      setOnboardingComplete(data?.onboarding_completed ?? false);
    };
    
    if (user?.id) {
      checkOnboarding();
    }
  }, [user?.id]);

  // Main access control logic
  useEffect(() => {
    // Wait for all auth/subscription checks to complete
    if (!isReady) return;

    console.log('[ProtectedRoute] Check access:', {
      user: !!user,
      isAdmin,
      isActive,
      pathname: location.pathname,
    });

    // Not logged in -> redirect to auth
    if (!user) {
      console.log('[ProtectedRoute] No user, redirecting to /auth');
      navigate('/auth', { replace: true });
      return;
    }

    // Admin required but user is not admin
    if (requireAdmin && !isAdmin) {
      console.log('[ProtectedRoute] Admin required but user is not admin');
      navigate('/dashboard', { replace: true });
      return;
    }

    // Routes that don't require subscription check
    const bypassRoutes = [
      '/pricing',
      '/auth',
      '/settings',
      '/profile',
      '/payment',
      '/ai-twin-onboarding',
      '/my-ai-twin/onboarding',
      '/my-ai-twin/train',
      '/my-ai-twin/pro-welcome'
    ];
    const shouldBypass = bypassRoutes.some(route => 
      location.pathname.startsWith(route)
    );

    if (shouldBypass || isAdmin) {
      console.log('[ProtectedRoute] Bypass route or admin, allowing access');
      return;
    }

    // Check subscription status - only for protected features
    // This allows users to log in and view basic pages without subscription
    // They will be redirected to pricing only when accessing premium features
    if (!isActive && !shouldBypass && !isAdmin) {
      console.log('[ProtectedRoute] No active subscription for protected content');
      navigate('/pricing', { 
        state: { 
          from: location.pathname,
          message: 'Please subscribe to access this feature' 
        },
        replace: true 
      });
      return;
    }

    // Check AI Twin onboarding completion
    if (!onboardingComplete && !location.pathname.startsWith('/ai-twin-onboarding') && !location.pathname.startsWith('/my-ai-twin')) {
      console.log('[ProtectedRoute] Onboarding not complete, redirecting');
      navigate('/my-ai-twin/onboarding', { replace: true });
      return;
    }
  }, [isReady, user, isAdmin, isActive, location.pathname, onboardingComplete, navigate, requireAdmin]);

  // Show loading spinner while auth checks are in progress
  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return <>{children}</>;
};
